/**
 * Which DOM elements count as "the customer is going to checkout".
 *
 * Every selector here was read off a real theme rather than guessed. The three reference
 * themes named in `PROMPT.md` are Dawn (and its many descendants), Impulse and Prestige; the
 * last two entries are the generic and opt-in escape hatches for everything else.
 *
 * Dawn source read for this list (github.com/Shopify/dawn, `main`, read 2026-09-14):
 * - `sections/main-cart-footer.liquid` — the cart page button:
 *     `<button type="submit" id="checkout" class="cart__checkout-button button" name="checkout"
 *      form="cart">`
 * - `snippets/cart-drawer.liquid` — the drawer button:
 *     `<button type="submit" id="CartDrawer-Checkout" class="cart__checkout-button button"
 *      name="checkout" form="CartDrawer-Form">`, inside `<form action="{{ routes.cart_url }}"
 *      id="CartDrawer-Form" class="cart__contents cart-drawer__form" method="post">`
 *
 * Note the consequence: in Dawn the checkout button is **outside** its form and associated
 * through the `form="…"` attribute, so a descendant selector like
 * `form[action$="/cart"] button[name="checkout"]` does not match it. That selector is kept for
 * the themes that do nest the button, and `button[name="checkout"]` catches Dawn.
 */

/** Opt-in attribute for custom themes: put it on any control that leads to checkout. */
export const PEAK_OPT_IN_ATTRIBUTE = 'data-peak-checkout';

/** Escape hatch: anything inside an element carrying this is never intercepted. */
export const PEAK_OPT_OUT_ATTRIBUTE = 'data-peak-ignore';

/**
 * The intercept targets, in the order they are documented in `docs/STOREFRONT.md`.
 *
 * Kept as one flat list because the DOM does the matching: they are joined into a single
 * selector list and handed to `closest()`, which is O(depth) regardless of list length.
 */
export const PEAK_CHECKOUT_SELECTORS: readonly string[] = [
  // Themes that nest the submit button inside the cart form (Impulse, Prestige, older Debut
  // derivatives).
  'form[action$="/cart"] button[name="checkout"]',
  'form[action$="/cart"] input[name="checkout"]',
  // Dawn and descendants: the button is associated to the form by id, not by nesting.
  'button[name="checkout"]',
  '#CartDrawer-Checkout',
  '.cart__checkout-button',
  // Drawer variants used by Dawn descendants and Impulse.
  '.cart-drawer__checkout',
  '.cart__submit',
  // Plain links to checkout (Prestige's drawer footer, minicart templates, custom themes).
  // Every other checkout URL shape is matched by `isCheckoutHref` on the resolved href.
  'a[href="/checkout"]',
  'a[href^="/checkout?"]',
  // Explicit opt-in for anything the above misses.
  '[' + PEAK_OPT_IN_ATTRIBUTE + ']',
];

/** The selector list as a single CSS selector, ready for `matches()` / `closest()`. */
export const PEAK_CHECKOUT_SELECTOR = PEAK_CHECKOUT_SELECTORS.join(',');

/**
 * The part of `Element` this module needs.
 *
 * Structural rather than `Element` so the matching rules can be unit-tested with plain fake
 * nodes; there is no jsdom in this project's Vitest setup.
 */
export type YcElementLike = {
  matches(selector: string): boolean;
  closest(selector: string): YcElementLike | null;
  getAttribute(name: string): string | null;
};

/** Does this exact element look like a checkout control? */
export function isCheckoutControl(element: YcElementLike): boolean {
  return element.matches(PEAK_CHECKOUT_SELECTOR);
}

/** Is this element (or an ancestor) explicitly excluded from interception? */
export function isOptedOut(element: YcElementLike): boolean {
  return element.closest('[' + PEAK_OPT_OUT_ATTRIBUTE + ']') !== null;
}

/**
 * The checkout control for an event target, or `null`.
 *
 * A click lands on whatever is under the cursor — very often a `<span>` inside the button — so
 * the nearest matching ancestor is the control, not the target itself.
 */
export function resolveCheckoutControl(target: YcElementLike | null): YcElementLike | null {
  if (target === null) return null;
  const control = target.closest(PEAK_CHECKOUT_SELECTOR);
  if (control === null) return null;
  if (isOptedOut(control)) return null;
  return control;
}

/** `/cart/c/{token}`, locale prefix optional; group 1 is the token. */
const CART_CHECKOUT_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart\/c\/([\w-]+)\/?$/i;

/**
 * `/cart/checkout`, locale prefix optional; group 1 is the cart path (`/cart`, `/en/cart`). Shopify
 * answers a `GET` with a redirect into its checkout (D372: Gonest's drawer link, 2026-10-30).
 */
const CART_TO_CHECKOUT_PATH = /^(\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart)\/checkout\/?$/i;

/**
 * `/checkout`, or `/checkouts/…` (a checkout Shopify already opened) other than the pages a shopper
 * reaches after paying; locale prefix optional. Those pages are named by `checkout.liquid`'s
 * documented `Shopify.Checkout.page` values (`thank_you`, `processing`) and by the post-purchase and
 * order-status surfaces, https://shopify.dev/docs/storefronts/themes/architecture/layouts/checkout-liquid
 * (read 2026-10-31); their URL shapes are not documented, so any spelling with `-` or `_` counts.
 */
const CHECKOUT_PATH =
  /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:checkout\/?|checkouts\/(?!.*(?:thank[-_]you|post[-_]purchase|processing|order[-_]status)).+)$/i;

/**
 * Does this path lead to Shopify's checkout?
 *
 * Four shapes, each with the optional `/{locale}/` prefix a shop on Shopify Markets serves
 * (`/en/…`, `/nl-nl/…`; `docs/SHOPIFY.md` §7.1 documents the same prefix on `cart.js`):
 *
 * - `/checkout` — what themes link to.
 * - `/cart/checkout` (D372) — a theme's or app's link that Shopify redirects into its checkout
 *   (observed on Gonest, 2026-10-30: `GET /cart/checkout` → 302 → Shop Pay / `/checkouts/cn/…`;
 *   not documented on shopify.dev). It names no cart, like `/checkout`.
 * - `/cart/c/{token}` — the Storefront API's `Cart.checkoutUrl`
 *   (`https://{shop}/cart/c/{token}?key={key}`, shopify.dev changelog "Storefront API
 *   Cart.checkoutUrl now contains key param"). Cart-drawer apps rewrite their checkout link to
 *   it (UpCart). The token is one path segment of letters, digits, `-` and `_`.
 * - `/checkouts/…` — a checkout Shopify has already opened (`/checkouts/cn/{token}`), except
 *   its thank-you, processing, post-purchase and order-status pages (`CHECKOUT_PATH`): the shopper
 *   has paid there, so taking such a link over could only cost them their confirmation.
 *
 * Deliberately **not** checkout (D204): `/cart` and its Ajax/form endpoints (`/cart/add`,
 * `/cart/change`, `/cart/update`), and the cart permalink `/cart/{variant}:{qty},…` — a
 * permalink builds its own cart from the URL rather than the shopper's current one, so handing
 * off `cart.js` would sell the wrong items. The `:` is outside the token alphabet, so a
 * permalink can never match. Nor is a product or page that happens to be called "checkout".
 */
export function isCheckoutPath(pathname: string): boolean {
  return (
    CHECKOUT_PATH.test(pathname) ||
    CART_CHECKOUT_PATH.test(pathname) ||
    CART_TO_CHECKOUT_PATH.test(pathname)
  );
}

/** `/checkout` alone, locale prefix optional. */
const PLAIN_CHECKOUT_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i;

/**
 * `/checkouts/{token}`, `/checkouts/c/{token}` or `/checkouts/cn/{token}`, locale prefix optional,
 * anything after the token allowed (a checkout step, a locale); group 1 is the token. Its paid
 * pages are excluded by `isCheckoutPath`.
 */
const CHECKOUTS_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkouts\/(?:cn?\/)?([\w-]+)(?:\/.*)?$/i;

/** The token a checkout href names, how strictly it must match `cart.js`'s, and the reason if not. */
export type PeakLinkToken = { token: string; strict: boolean; mismatch: string };

/**
 * The cart token a checkout href names, for the handoff's token check, with the reason the report
 * carries when it is not `cart.js`'s; `null` for a shape that names no cart (`/checkout`,
 * `/cart/checkout`).
 *
 * Since D385 a mismatch no longer sends the shopper to the link: Shopify's checkout is never a
 * fallback, so the session cart goes to Peak (the cart page when it is empty) and the report says
 * the link named another one. A "Buy it now" flow's own cart is lost that way; that is the owner's
 * call, and the express buttons that start such a flow are hidden.
 *
 * - `/cart/c/{token}` (D204): `cart_token_mismatch`; a `cart.js` with no string token passes
 *   (`isSameCartToken`).
 * - `/checkouts/…/{token}` (D259): `checkouts_token_mismatch`, and `strict` — a `cart.js` with no
 *   token does not pass. That Shopify puts the **cart** token in this path is not documented on
 *   shopify.dev (searched 2026-10-17), so only a proven match counts as the session cart.
 */
export function linkTokenOf(href: string | null, pageOrigin: string): PeakLinkToken | null {
  const url = sameOriginUrl(href, pageOrigin);
  if (url === null) return null;
  const cart = CART_CHECKOUT_PATH.exec(url.pathname);
  if (cart !== null) {
    return { token: cart[1] as string, strict: false, mismatch: 'cart_token_mismatch' };
  }
  const opened = isCheckoutPath(url.pathname) ? CHECKOUTS_PATH.exec(url.pathname) : null;
  return opened === null
    ? null
    : { token: opened[1] as string, strict: true, mismatch: 'checkouts_token_mismatch' };
}

/**
 * Which of the four checkout shapes a same-origin href has, for the fallback report (D247):
 * `checkout` (`/checkout`), `cart_checkout` (`/cart/checkout`, D372), `cart_c`
 * (`/cart/c/{token}`), `checkouts` (`/checkouts/…`), or `none` for anything else, a missing href
 * or another origin. The shape only: never the token, the query or the locale prefix.
 */
export function checkoutLinkKind(
  href: string | null,
  pageOrigin: string,
): 'checkout' | 'cart_checkout' | 'cart_c' | 'checkouts' | 'none' {
  const url = sameOriginUrl(href, pageOrigin);
  if (url === null) return 'none';
  if (PLAIN_CHECKOUT_PATH.test(url.pathname)) return 'checkout';
  if (CART_TO_CHECKOUT_PATH.test(url.pathname)) return 'cart_checkout';
  if (CART_CHECKOUT_PATH.test(url.pathname)) return 'cart_c';
  return CHECKOUT_PATH.test(url.pathname) ? 'checkouts' : 'none';
}

/**
 * The cart path of a same-origin `/cart/checkout` href (`/cart`, `/en/cart`) and its query, for
 * carrying the query's cart fields through `{cart}/update.js` (D372, `./cart-form.ts`
 * `linkUpdateFields`); `null` for every other shape and for other origins.
 */
export function cartCheckoutLinkOf(
  href: string | null,
  pageOrigin: string,
): { cartPath: string; search: string } | null {
  const url = sameOriginUrl(href, pageOrigin);
  const match = url === null ? null : CART_TO_CHECKOUT_PATH.exec(url.pathname);
  return match === null ? null : { cartPath: match[1] as string, search: (url as URL).search };
}

/** The href resolved against the page, or `null` when it is unusable or on another origin. */
function sameOriginUrl(href: string | null, pageOrigin: string): URL | null {
  if (href === null || href === '') return null;
  // A theme may render an absolute, a root-relative or a protocol-relative href.
  try {
    const url = new URL(href, pageOrigin);
    return url.origin === pageOrigin ? url : null;
  } catch {
    return null;
  }
}

/**
 * Is this href a navigation to checkout, given the page's own origin? A link, a form and, since
 * D385, a scripted navigation (`onNavigate`, D212) all go by this one answer.
 *
 * Cross-origin links are never intercepted: an `/checkout` on someone else's domain is not
 * this shop's checkout. That includes the shop's own `*.myshopify.com` or `checkout.` host seen
 * from its custom domain — the cart cookie lives on the page's origin, and no theme or app seen
 * so far links across (D204).
 */
export function isCheckoutHref(href: string | null, pageOrigin: string): boolean {
  const url = sameOriginUrl(href, pageOrigin);
  return url !== null && isCheckoutPath(url.pathname);
}

/**
 * The cart token of a same-origin `/cart/c/{token}` href; `null` for every other shape.
 *
 * The handoff reads the shopper's cart from `cart.js`; this lets it confirm the link names that
 * same cart before it replaces the navigation (`isSameCartToken`).
 */
export function cartTokenFromCheckoutHref(href: string | null, pageOrigin: string): string | null {
  const url = sameOriginUrl(href, pageOrigin);
  const match = url === null ? null : CART_CHECKOUT_PATH.exec(url.pathname);
  return match?.[1] ?? null;
}

/**
 * Does `cart.js`'s `token` name the cart of a `/cart/c/{token}` link?
 *
 * `cart.js` may carry its token with the `?key=…` suffix that `checkoutUrl` appends, so only
 * the part before `?` is compared. A `cart.js` without a string token cannot be checked and
 * counts as a match: the check exists to catch a link to a *different* cart, not to add a new
 * way to fail — except under `strict` (a `/checkouts/cn/` URL, D259), where only a proven match
 * counts.
 */
export function isSameCartToken(linkToken: string, cartToken: unknown, strict = false): boolean {
  if (typeof cartToken !== 'string' || cartToken === '') return !strict;
  return cartToken.split('?')[0] === linkToken;
}
